import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faSearch } from "@fortawesome/free-solid-svg-icons";
import { primaryColor, secondaryColor } from "../../../utils/comon";

const CustomMultiSelect = ({
    label,
    options = [],
    value = [], // Expected to be an array of objects
    onChange,
    placeholder = "Select options",
    error,
    errorMessage,
    required = false,
    isDisabled = false
}) => {
    const [open, setOpen] = useState(false);
    const [isAbove, setIsAbove] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, bottom: 0 });
    
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    const updateCoords = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setCoords({
                top: rect.top + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
                bottom: rect.bottom + window.scrollY
            });

            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 250 && rect.top > spaceBelow) {
                setIsAbove(true);
            } else {
                setIsAbove(false);
            }
        }
    };

    useEffect(() => {
        if (open) {
            updateCoords();
            window.addEventListener("scroll", updateCoords, true);
            window.addEventListener("resize", updateCoords);
            // Focus search input on open
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
        if (!open) {
            setSearchTerm("");
        }
        return () => {
            window.removeEventListener("scroll", updateCoords, true);
            window.removeEventListener("resize", updateCoords);
        };
    }, [open]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target) &&
                dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = options.filter(opt => 
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const isAllSelected = filteredOptions.length > 0 && filteredOptions.every(opt => 
        value.some(v => v.value === opt.value)
    );

    const isSomeSelected = filteredOptions.some(opt => 
        value.some(v => v.value === opt.value)
    );

    const handleSelectAll = (e) => {
        e.stopPropagation();
        if (isAllSelected) {
            // Deselect all filtered
            const filteredValues = filteredOptions.map(o => o.value);
            onChange(value.filter(v => !filteredValues.includes(v.value)));
        } else {
            // Select all filtered
            const newValues = [...value];
            filteredOptions.forEach(opt => {
                if (!newValues.some(v => v.value === opt.value)) {
                    newValues.push(opt);
                }
            });
            onChange(newValues);
        }
    };

    const handleSelect = (option, e) => {
        e.stopPropagation();
        const isSelected = value.some(v => v.value === option.value);
        if (isSelected) {
            onChange(value.filter(v => v.value !== option.value));
        } else {
            onChange([...value, option]);
        }
    };

    const dropdownContent = open && (
        <div
            ref={dropdownRef}
            className={`fixed z-[99999] bg-white dark:bg-cardbg border border-[#E2E8F0] dark:border-bordercolor rounded-xl shadow-2xl flex flex-col`}
            style={{
                top: isAbove ? coords.top - 12 : coords.bottom + 12,
                left: coords.left,
                width: coords.width,
                maxHeight: "300px",
                transform: isAbove ? "translateY(-100%)" : "none"
            }}
        >
            {/* Search Bar */}
            <div className="p-3 border-b border-gray-100 dark:border-bordercolor flex-shrink-0">
                <div className="relative">
                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                    <input 
                        ref={searchInputRef}
                        type="text" 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search..."
                        className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-lg text-sm focus:outline-none focus:bg-white dark:focus:bg-cardbg text-[#0F172A] dark:text-textprimary transition-colors"
                        style={{ outlineColor: primaryColor() }}
                    />
                </div>
            </div>

            <style>{`
                .custom-multi-scrollbar::-webkit-scrollbar { width: 5px; }
                .custom-multi-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
                .custom-multi-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            `}</style>
            
            <div className="overflow-y-auto custom-multi-scrollbar py-2 flex-1">
                {/* Select All Option */}
                {filteredOptions.length > 0 && (
                    <div
                        onClick={handleSelectAll}
                        className="px-4 py-2 text-[13px] font-semibold text-gray-700 dark:text-textsecondary cursor-pointer flex items-center gap-3 transition-colors"
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = secondaryColor(); e.currentTarget.style.color = primaryColor(); }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; e.currentTarget.style.color = ''; }}
                    >
                        <div 
                            className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${!isAllSelected && !isSomeSelected ? "border-gray-300 bg-white" : ""}`}
                            style={isAllSelected || isSomeSelected ? { backgroundColor: primaryColor(), borderColor: primaryColor() } : {}}
                        >
                            {isAllSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                            {!isAllSelected && isSomeSelected && <div className="w-2 h-0.5 bg-white rounded-full"></div>}
                        </div>
                        <span>Select All {searchTerm ? "Filtered" : ""}</span>
                    </div>
                )}

                {/* Options */}
                {filteredOptions.map((opt) => {
                    const isSelected = value.some(v => v.value === opt.value);
                    return (
                        <div
                            key={opt.value}
                            onClick={(e) => handleSelect(opt, e)}
                            className={`px-4 py-2 text-[13px] cursor-pointer flex items-center gap-3 transition-colors duration-150 text-[#475569] dark:text-textsecondary`}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = secondaryColor();
                                e.currentTarget.style.color = primaryColor();
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '';
                                e.currentTarget.style.color = '';
                            }}
                        >
                            <div 
                                className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${!isSelected ? "border-gray-300 bg-white" : ""}`}
                                style={isSelected ? { backgroundColor: primaryColor(), borderColor: primaryColor() } : {}}
                            >
                                {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                            </div>
                            <span>{opt.label}</span>
                        </div>
                    );
                })}
                {filteredOptions.length === 0 && (
                    <div className="px-4 py-3 text-xs text-gray-400 text-center italic">
                        No results found
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div
            className={`w-full relative outline-none ${isDisabled ? "opacity-60 pointer-events-none" : ""}`}
            ref={containerRef}
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (!open) setOpen(true);
                }
            }}
        >
            {label && (
                <label className="text-xs font-semibold text-[#0F172A] dark:text-textprimary mb-1 block uppercase tracking-tight">
                    {label} {required && <span className="text-red-700">*</span>}
                </label>
            )}

            <div
                onClick={() => !isDisabled && setOpen(!open)}
                className={`border border-slate-200 dark:border-bordercolor bg-white dark:bg-actionitembg rounded-md px-3 py-2 w-full text-sm cursor-pointer flex justify-between items-center transition-all duration-200 ${error ? "border-red-500" : "hover:border-gray-400"}`}
            >
                <div className="flex items-center gap-2 overflow-hidden mr-2">
                    <span className={`truncate ${value.length > 0 ? "text-[#0F172A] dark:text-textprimary" : "text-gray-400"}`}>
                        {value.length > 0 ? `${value.length} selected` : placeholder}
                    </span>
                </div>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`text-gray-400 text-xs transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                />
            </div>

            {error && (
                <p className="text-red-500 text-[10px] mt-1 font-medium">
                    {typeof error === 'string' ? error : errorMessage}
                </p>
            )}

            {open && createPortal(dropdownContent, document.body)}
        </div>
    );
};

export default CustomMultiSelect;
